import { ref, type Ref } from 'vue';
import { ElMessage } from 'element-plus';
import { extractApiError } from '@/api/client';

/**
 * Composable generique pour le pattern "loading + try/catch + ElMessage.error"
 * present dans 15+ vues du gameplay. Centralise la gestion d'erreur axios via
 * extractApiError et expose un loading reactif consommable par v-loading.
 *
 * Usage typique dans une vue :
 *
 *   const { loading, run } = useAsyncLoad();
 *   async function load() {
 *     await run(async () => {
 *       creatures.value = await fetchCreatureCatalog();
 *     });
 *   }
 *   onMounted(load);
 *
 * Variante avec valeur retournee (utile pour assigner directement un ref) :
 *
 *   const data = ref<X[]>([]);
 *   await run(async () => { data.value = await api.fetchX(); });
 *
 * Le composable conserve volontairement une API minimale -- les vues qui ont
 * besoin de plusieurs loaders independants peuvent en instancier plusieurs.
 */
export interface UseAsyncLoadOptions {
  /** Active le toast d'erreur ElMessage.error (default: true). Mettre a false
   * si la vue veut gerer l'erreur visuellement (badge, retry button). */
  showErrorToast?: boolean;
  /** Prefix pour les messages d'erreur, ex "Inventaire :" => "Inventaire : ...".
   * Permet a l'utilisateur d'identifier la zone qui a echoue. */
  errorPrefix?: string;
}

export interface UseAsyncLoadReturn {
  /** True pendant l'execution de run(). Bind sur v-loading. */
  loading: Ref<boolean>;
  /** Derniere erreur capturee (lisible via extractApiError). Reset a chaque run(). */
  lastError: Ref<string | null>;
  /**
   * Execute la callback fournie en gerant loading + erreurs. Retourne le
   * resultat de la callback (ou null si erreur). Ne re-throw PAS par defaut --
   * la vue continue de tourner avec un etat coherent.
   */
  run: <T>(fn: () => Promise<T>) => Promise<T | null>;
}

export function useAsyncLoad(options: UseAsyncLoadOptions = {}): UseAsyncLoadReturn {
  const { showErrorToast = true, errorPrefix } = options;

  const loading = ref(false);
  const lastError = ref<string | null>(null);

  async function run<T>(fn: () => Promise<T>): Promise<T | null> {
    loading.value = true;
    lastError.value = null;
    try {
      return await fn();
    } catch (err) {
      const msg = extractApiError(err);
      lastError.value = msg;
      if (showErrorToast) {
        ElMessage.error(errorPrefix ? `${errorPrefix} ${msg}` : msg);
      }
      // On log dans la console pour le dev meme si le toast est desactive :
      // un fail silencieux est tres difficile a debugger en prod.
      console.error('[useAsyncLoad]', err);
      return null;
    } finally {
      loading.value = false;
    }
  }

  return { loading, lastError, run };
}
